import {
  BATCH_METHOD_EIP5792,
  BATCH_METHOD_EIP7702,
  getAtomicRequiredOverride,
  getBatcherContractOverride,
  getBatchModeOverride,
  getColorSchemeOverride,
  getCurrencyOverride,
  getEnableFeeOverride,
  getHeightOverride,
  getLocaleOverride,
  getWalletModeOverride,
  getWidthOverride,
  isInIframe,
} from "@blockdaemon/widget-host";
import {
  SharedWidgetConfigProvider,
  type SharedWidgetAppearance,
  useSharedWidgetAppearance,
} from "@blockdaemon/widget-shell";
import { Center, Loader, useMantineColorScheme } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import type { ReactNode } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useRef,
  useState,
} from "react";

import {
  createSecureMessageListener,
  sendSecurePostMessage,
} from "../../../shared/utils/postmessage-validation";
import {
  AUTH_TOKEN_REQUEST_MESSAGE_TYPE,
  AUTH_TOKEN_RESPONSE_MESSAGE_TYPE,
  AUTH_TOKEN_TIMEOUT_MS,
  type AuthTokenMessage,
  getNextAuthTokenRefreshDelayMs,
} from "../../../shared/utils/widget-auth-protocol";
import type {
  NormalizedWidgetFeatures,
  WidgetAppearance,
  WidgetConfig,
  WidgetFeatures,
} from "../lib/config-schema";
import { normalizeFeatures } from "../lib/config-schema";
import { EARN_CONFIG_QUERY_ROOT, useConfig, WidgetApiError } from "../utils/config-loader";

interface WidgetConfigContextType {
  config: WidgetConfig;
  updateConfig: (updates: Partial<WidgetConfig>) => void;
  updateFeatures: (updates: Partial<WidgetFeatures>) => void;
  updateAppearance: (updates: Partial<WidgetAppearance>) => void;
  toggleFeature: (feature: "staking" | "swap" | "defi") => void;
  resetConfig: () => void;
  features: NormalizedWidgetFeatures;
  appearance: WidgetAppearance;
}

// Exported for testing purposes - allows test providers to use the same context
export const WidgetConfigContext = createContext<WidgetConfigContextType | undefined>(undefined);

const AUTH_TOKEN_TIMEOUT_ERROR_MESSAGE =
  "Timed out waiting for the host app to provide an auth token.";
const AUTH_TOKEN_MISSING_ERROR_MESSAGE =
  "The host app did not provide an auth token for this widget.";
type AuthRequestReason = "initial" | "refresh";

interface AuthState {
  authToken: string | null;
  authTokenVersion: number;
  hasRequestedAuthToken: boolean;
  authRequestNonce: number;
  authRequestReason: AuthRequestReason | null;
  authErrorMessage: string | null;
  isRequestingAuthToken: boolean;
}

type AuthAction =
  | { type: "request-start"; reason: AuthRequestReason }
  | { type: "request-timeout"; errorMessage: string }
  | {
      type: "request-resolved";
      resolvedAuthToken: ReturnType<typeof resolveRequestedAuthToken>;
    };

const initialAuthState: AuthState = {
  authToken: null,
  authTokenVersion: 0,
  hasRequestedAuthToken: false,
  authRequestNonce: 0,
  authRequestReason: null,
  authErrorMessage: null,
  isRequestingAuthToken: false,
};

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case "request-start":
      return {
        ...state,
        hasRequestedAuthToken: action.reason === "initial" ? true : state.hasRequestedAuthToken,
        authErrorMessage: null,
        authRequestReason: action.reason,
        isRequestingAuthToken: true,
        authRequestNonce: state.authRequestNonce + 1,
      };
    case "request-timeout":
      return {
        ...state,
        authRequestReason: null,
        isRequestingAuthToken: false,
        authToken: null,
        authErrorMessage: action.errorMessage,
      };
    case "request-resolved":
      return {
        ...state,
        authRequestReason: null,
        isRequestingAuthToken: false,
        authErrorMessage: action.resolvedAuthToken.authErrorMessage,
        authToken: action.resolvedAuthToken.authToken,
        authTokenVersion:
          state.authTokenVersion + (action.resolvedAuthToken.shouldIncrementTokenVersion ? 1 : 0),
      };
  }
}

function isAuthTokenMessage(data: unknown): data is AuthTokenMessage {
  if (!data || typeof data !== "object") {
    return false;
  }

  const message = data as Partial<AuthTokenMessage>;

  return (
    message.type === AUTH_TOKEN_RESPONSE_MESSAGE_TYPE &&
    typeof message.data === "object" &&
    message.data !== null &&
    typeof message.data.token === "string"
  );
}

export function resolveRequestedAuthToken(token: string) {
  const nextToken = token.trim();
  if (!nextToken) {
    return {
      authErrorMessage: AUTH_TOKEN_MISSING_ERROR_MESSAGE,
      authToken: null,
      shouldIncrementTokenVersion: false,
    };
  }

  return {
    authErrorMessage: null,
    authToken: nextToken,
    shouldIncrementTokenVersion: true,
  };
}

export function shouldScheduleAuthRefresh({
  authErrorMessage,
  authToken,
  error,
  isEmbedded,
  isLoading,
  isRequestingAuthToken,
  serverConfig,
}: {
  authErrorMessage: string | null;
  authToken: string | null;
  error: unknown;
  isEmbedded: boolean;
  isLoading: boolean;
  isRequestingAuthToken: boolean;
  serverConfig: WidgetConfig | undefined;
}) {
  return (
    Boolean(authToken) &&
    Boolean(serverConfig) &&
    !isLoading &&
    !error &&
    isEmbedded &&
    !authErrorMessage &&
    !isRequestingAuthToken
  );
}

export function hasWidgetAuthFailure({
  authErrorMessage,
  authRequestError,
  authToken,
  error,
  hasRequestedAuthToken,
  isEmbedded,
}: {
  authErrorMessage: string | null;
  authRequestError: WidgetApiError | null;
  authToken: string | null;
  error: unknown;
  hasRequestedAuthToken: boolean;
  isEmbedded: boolean;
}) {
  return (
    Boolean(authErrorMessage) ||
    (Boolean(authRequestError) && !isEmbedded) ||
    Boolean(
      error instanceof WidgetApiError &&
      [401, 403, 503].includes(error.status) &&
      (hasRequestedAuthToken || Boolean(authToken)),
    )
  );
}

export const WidgetConfigProvider = ({ children }: { children: ReactNode }) => {
  "use no memo";
  const [authState, dispatchAuth] = useReducer(authReducer, initialAuthState);
  const isEmbedded = isInIframe();
  const {
    authToken,
    authTokenVersion,
    hasRequestedAuthToken,
    authRequestNonce,
    authRequestReason,
    authErrorMessage,
    isRequestingAuthToken,
  } = authState;
  const {
    data: serverConfig,
    isLoading: isEarnConfigLoading,
    error: earnConfigError,
  } = useConfig({
    token: authToken ?? undefined,
    tokenVersion: authTokenVersion,
  });
  const configQueryError = earnConfigError;
  const isConfigLoading = isEarnConfigLoading;
  const queryClient = useQueryClient();
  const [config, setConfig] = useState<WidgetConfig | null>(null);
  const { setColorScheme } = useMantineColorScheme();
  const hasInitializedColorScheme = useRef(false);

  // Get overrides from URL parameters (for customer embedding)
  const urlColorScheme = getColorSchemeOverride();
  const urlWidth = getWidthOverride();
  const urlHeight = getHeightOverride();
  const urlCurrency = getCurrencyOverride();
  const urlLocale = getLocaleOverride();
  const urlWalletMode = getWalletModeOverride();
  const urlBatchMode = getBatchModeOverride();
  const urlBatcherContract = getBatcherContractOverride();
  const urlAtomicRequired = getAtomicRequiredOverride();
  const urlEnableFee = getEnableFeeOverride();

  const currentConfig = config ?? serverConfig;

  // Create final config with URL parameter overrides applied
  const finalConfig = currentConfig
    ? {
        ...currentConfig,
        features: {
          ...currentConfig.features,
          // Override wallet mode with URL parameter if provided
          walletMode: urlWalletMode || currentConfig.features.walletMode,
          // Override batchOption with URL parameter if provided
          ...(urlBatchMode === "off"
            ? { batchOption: undefined }
            : urlBatchMode === BATCH_METHOD_EIP5792
              ? { batchOption: { eip5792: { atomicRequired: urlAtomicRequired ?? false } } }
              : urlBatchMode === BATCH_METHOD_EIP7702
                ? {
                    batchOption: {
                      eip7702: {
                        batcherContract:
                          urlBatcherContract ??
                          currentConfig.features.batchOption?.eip7702?.batcherContract ??
                          "",
                      },
                    },
                  }
                : {}),
          // Override swap fee enabled with URL parameter if provided
          ...(urlEnableFee !== null && typeof currentConfig.features.swap === "object"
            ? {
                swap: {
                  ...currentConfig.features.swap,
                  fees: {
                    ...currentConfig.features.swap.fees,
                    enabled: urlEnableFee,
                  },
                },
              }
            : {}),
        },
        appearance: {
          ...currentConfig.appearance,
          // Override theme with URL parameter if provided
          theme: urlColorScheme || currentConfig.appearance.theme,
          // Override width with URL parameter if provided
          width: urlWidth !== null ? urlWidth : currentConfig.appearance.width,
          // Override height with URL parameter if provided
          height: urlHeight !== null ? urlHeight : currentConfig.appearance.height,
          // Override currency with URL parameter if provided
          currency: urlCurrency || currentConfig.appearance.currency,
          // Override locale with URL parameter if provided
          locale: urlLocale || currentConfig.appearance.locale,
        },
      }
    : null;

  // Initialize Mantine color scheme when config loads
  // Priority: URL override > Config theme > localStorage (user preference)
  useEffect(() => {
    if (hasInitializedColorScheme.current || !finalConfig?.appearance?.theme) {
      return;
    }

    // URL override takes highest priority
    if (urlColorScheme) {
      setColorScheme(urlColorScheme);
      localStorage.setItem("mantine-color-scheme", urlColorScheme);
      hasInitializedColorScheme.current = true;
      return;
    }

    const configTheme = finalConfig.appearance.theme;
    const storedColorScheme = localStorage.getItem("mantine-color-scheme");

    // Use config theme as default, but respect user preference if it differs
    if (storedColorScheme === "light" || storedColorScheme === "dark") {
      if (storedColorScheme !== configTheme) {
        // User has set a different preference, use it
        setColorScheme(storedColorScheme);
      } else {
        // Matches config, use config theme (ensures config changes are respected)
        setColorScheme(configTheme);
        localStorage.setItem("mantine-color-scheme", configTheme);
      }
    } else {
      // No user preference, use config theme
      setColorScheme(configTheme);
      localStorage.setItem("mantine-color-scheme", configTheme);
    }

    hasInitializedColorScheme.current = true;
  }, [finalConfig?.appearance?.theme, urlColorScheme, setColorScheme]);

  const updateConfig = (updates: Partial<WidgetConfig>) => {
    if (!currentConfig) return;
    setConfig({
      ...currentConfig,
      ...updates,
    });
  };

  const updateFeatures = (updates: Partial<WidgetFeatures>) => {
    if (!currentConfig) return;
    setConfig({
      ...currentConfig,
      features: { ...currentConfig.features, ...updates },
    });
  };

  const updateAppearance = (updates: Partial<WidgetAppearance>) => {
    if (!currentConfig) return;

    setConfig({
      ...currentConfig,
      appearance: { ...currentConfig.appearance, ...updates },
    });
  };

  const toggleFeature = (feature: "staking" | "swap" | "defi") => {
    if (!currentConfig) return;

    const currentValue = currentConfig.features[feature];

    // Compute proposed value preserving object form when present
    const proposedValue =
      typeof currentValue === "boolean"
        ? !currentValue
        : { ...currentValue, enabled: !currentValue.enabled };

    // Prevent disabling last enabled core feature
    const proposedFeatures = {
      ...currentConfig.features,
      [feature]: proposedValue,
    } as WidgetFeatures;
    const normalized = normalizeFeatures(proposedFeatures);
    const enabledCount = [normalized.staking, normalized.swap, normalized.defi].filter(
      Boolean,
    ).length;
    if (enabledCount === 0) return;

    updateFeatures({ [feature]: proposedValue } as Partial<WidgetFeatures>);
  };

  const resetConfig = () => {
    // Reset to null and refetch from server
    setConfig(null);

    void queryClient.invalidateQueries({ queryKey: [...EARN_CONFIG_QUERY_ROOT] });
  };

  const authRequestError =
    configQueryError instanceof WidgetApiError && [401, 403].includes(configQueryError.status)
      ? configQueryError
      : null;
  const shouldRequestInitialAuthToken =
    Boolean(authRequestError) &&
    !hasRequestedAuthToken &&
    !authToken &&
    !authErrorMessage &&
    isEmbedded &&
    !isRequestingAuthToken;

  const requestAuthToken = useCallback(
    (reason: AuthRequestReason) => {
      if (!isEmbedded || isRequestingAuthToken) {
        return;
      }

      dispatchAuth({ type: "request-start", reason });
    },
    [isEmbedded, isRequestingAuthToken],
  );

  useEffect(() => {
    if (!shouldRequestInitialAuthToken) {
      return;
    }

    requestAuthToken("initial");
  }, [requestAuthToken, shouldRequestInitialAuthToken]);

  useEffect(() => {
    if (!authRequestNonce || !authRequestReason) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      dispatchAuth({
        type: "request-timeout",
        errorMessage: AUTH_TOKEN_TIMEOUT_ERROR_MESSAGE,
      });
    }, AUTH_TOKEN_TIMEOUT_MS);

    const handleMessage = createSecureMessageListener<AuthTokenMessage>(
      (message) => {
        const resolvedAuthToken = resolveRequestedAuthToken(message.data.token);
        window.clearTimeout(timeoutId);
        dispatchAuth({ type: "request-resolved", resolvedAuthToken });
      },
      {
        expectedTypes: [AUTH_TOKEN_RESPONSE_MESSAGE_TYPE],
        validateData: isAuthTokenMessage,
      },
    );

    window.addEventListener("message", handleMessage);
    sendSecurePostMessage(window.parent, { type: AUTH_TOKEN_REQUEST_MESSAGE_TYPE });

    return () => {
      window.clearTimeout(timeoutId);
      window.removeEventListener("message", handleMessage);
    };
  }, [authRequestNonce, authRequestReason]);

  const shouldRefreshAuthToken = shouldScheduleAuthRefresh({
    authErrorMessage,
    authToken,
    error: configQueryError,
    isEmbedded,
    isLoading: isConfigLoading,
    isRequestingAuthToken,
    serverConfig,
  });

  useEffect(() => {
    if (!shouldRefreshAuthToken || !authToken) {
      return;
    }

    // Re-schedule after each successful token response, even when the host returns the same JWT.
    void authTokenVersion;
    const refreshDelayMs = getNextAuthTokenRefreshDelayMs(authToken);
    const timeoutId = window.setTimeout(() => {
      requestAuthToken("refresh");
    }, refreshDelayMs);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [authToken, authTokenVersion, requestAuthToken, shouldRefreshAuthToken]);

  const isAwaitingAuthToken =
    shouldRequestInitialAuthToken ||
    (authRequestReason === "initial" && isRequestingAuthToken) ||
    (hasRequestedAuthToken && !authToken && !authErrorMessage);
  const hasAuthFailure = hasWidgetAuthFailure({
    authErrorMessage,
    authRequestError,
    authToken,
    error: configQueryError,
    hasRequestedAuthToken,
    isEmbedded,
  });

  if (isConfigLoading || isAwaitingAuthToken) {
    return (
      <Center h="100vh">
        <Loader size="lg" />
      </Center>
    );
  }

  if (hasAuthFailure) {
    return null;
  }

  if (configQueryError) {
    throw new Error(
      configQueryError instanceof Error ? configQueryError.message : "Failed to load widget config",
    );
  }

  if (!finalConfig) {
    throw new Error("Widget config not found");
  }

  // Typed adapter: SharedWidgetAppearance is broader (e.g. locale: string) than
  // WidgetAppearance (locale: "en-US"|"ko-KR"|"ja-JP"). The cast inside is safe
  // because the shared type only carries fields the earn appearance already handles.
  const updateSharedAppearance = (updates: Partial<SharedWidgetAppearance>) => {
    updateAppearance(updates as Partial<WidgetAppearance>);
  };

  return (
    <WidgetConfigContext.Provider
      value={{
        config: finalConfig,
        updateConfig,
        updateFeatures,
        updateAppearance,
        toggleFeature,
        resetConfig,
        features: normalizeFeatures(finalConfig.features),
        appearance: finalConfig.appearance,
      }}
    >
      <SharedWidgetConfigProvider
        value={{
          config: finalConfig,
          appearance: finalConfig.appearance,
          updateAppearance: updateSharedAppearance,
          resetConfig,
          isLoading: false,
        }}
      >
        {children}
      </SharedWidgetConfigProvider>
    </WidgetConfigContext.Provider>
  );
};

export const useWidgetConfig = () => {
  const context = useContext(WidgetConfigContext);
  if (context === undefined) {
    throw new Error("useWidgetConfig must be used within a WidgetConfigProvider");
  }
  return context;
};

export const useFeatures = () => {
  const { features, toggleFeature, updateFeatures } = useWidgetConfig();
  return { features, toggleFeature, updateFeatures };
};

export const useAppearance = () => useSharedWidgetAppearance();
